import type {
  DeviceChangeResourceLogicalDeviceApi,
  GenericIdReadOnlyApi,
  PhysicalDeviceResourceReadApi,
} from '@ingenico/api-sdk-device-management-private-v2';

export type GenericDatetime = Date;

export type GenericUuidReadOnly = string;

export type ReadOnlyDate = Date;

export type GenericUuid = string;

export type SearchField = string;

export const enumDeploymentUnitStatus = {
  CANCELED: 'CANCELED',
  COMPLETED: 'COMPLETED',
  CREATED: 'CREATED',
  FAILED: 'FAILED',
  PAUSED: 'PAUSED',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  SCHEDULED: 'SCHEDULED',
  WAITING_ATTACHMENT: 'WAITING_ATTACHMENT',
} as const;
export type EnumDeploymentUnitStatus = (typeof enumDeploymentUnitStatus)[keyof typeof enumDeploymentUnitStatus];

export const enumReferenceMode = {
  CUSTOM: 'CUSTOM',
  ID: 'ID',
} as const;
export type EnumReferenceMode = (typeof enumReferenceMode)[keyof typeof enumReferenceMode];

export const enumConfigurationResourceSoftwareItem = {
  CONFIGURATION_MODULE: 'CONFIGURATION_MODULE',
  SOFTWARE_VERSION_NO_PARAM: 'SOFTWARE_VERSION_NO_PARAM',
} as const;
export type EnumConfigurationResourceSoftwareItem =
  (typeof enumConfigurationResourceSoftwareItem)[keyof typeof enumConfigurationResourceSoftwareItem];

export const searchFilterFilterType = {
  array: 'array',
  boolean: 'boolean',
  fulltext: 'fulltext',
  integer: 'integer',
  string: 'string',
} as const;
export type SearchFilterFilterType = (typeof searchFilterFilterType)[keyof typeof searchFilterFilterType];

export const searchOperator = {
  contains: 'contains',
  equals: 'equals',
  exists: 'exists',
  greaterThan: 'greaterThan',
  in: 'in',
  lowerThan: 'lowerThan',
  notEquals: 'notEquals',
  notExists: 'notExists',
} as const;
export type SearchOperator = (typeof searchOperator)[keyof typeof searchOperator];

export const searchRequestSortOrder = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const;
export type SearchRequestSortOrder = (typeof searchRequestSortOrder)[keyof typeof searchRequestSortOrder];
export interface SearchRequestSort {
  /**
   * @description Technical name of the field with its path on which the sort is applied. Note that for a string field, the suffix .keyword shall be added.
   * @type string | undefined
   */
  readonly field?: string;
  /**
   * @type string | undefined
   */
  readonly order?: SearchRequestSortOrder;
}
/**
 * @description Filter to be applied to the search.
 */
export interface SearchFilter {
  /**
   * @description Field type to be filtered, array means that the field can be enumerated.
   * @type string
   */
  readonly filterType: SearchFilterFilterType;
  /**
   * @type string
   */
  readonly name: SearchField;
  /**
   * @type string
   */
  readonly operator: SearchOperator;
  /**
   * @description value type wrt filterType: <br> - \"string\" => string <br> - \"array\" => array of strings <br> - \"string\" and operator \"contains\" => part of string or the whole string <br> - \"boolean\" => true or false. Everything JSON stringify
   * @type string
   */
  readonly value: string;
}
export interface SearchRequest {
  /**
   * @type array
   */
  readonly filters: SearchFilter[];
  /**
   * @type object
   */
  readonly sort: SearchRequestSort;
}

export interface DeploymentUnitResourceMinimalV2 {
  /**
   * @type string, date-time
   */
  readonly creationDate: ReadOnlyDate;
  /**
   * @type string, uuid
   */
  readonly deploymentRequestId: GenericUuidReadOnly;
  /**
   * @type string | undefined
   */
  readonly description?: string;
  /**
   * @type string, uuid
   */
  readonly deviceId: GenericUuidReadOnly;
  /**
   * @type string | undefined, date-time
   */
  readonly endDate?: GenericDatetime;
  /**
   * @type string, uuid
   */
  readonly id: GenericUuidReadOnly;
  /**
   * @type string
   */
  readonly name: string;
  /**
   * @type string | undefined, date-time
   */
  readonly startDate?: GenericDatetime;
  /**
   * @type string
   */
  readonly status: EnumDeploymentUnitStatus;
  /**
   * @type string | undefined
   */
  readonly statusDetail?: string;
}
export interface DeploymentRequestResourceReferenceMinimalV2 {
  /**
   * @type string, uuid
   */
  readonly id: GenericUuid;
}
export type DeploymentRequestResourceReferenceV2 = DeploymentRequestResourceReferenceMinimalV2 & {
  /**
   * @type string
   */
  readonly description: string;
  /**
   * @type string, uuid
   */
  readonly id: GenericUuid;
  /**
   * @type string
   */
  readonly name: string;
};
export interface ConfigurationResourceCompositionItemMinimalV2 {
  /**
   * @type string, uuid
   */
  readonly configurationId: GenericUuid;
}
export interface ConfigurationResourceSoftwareVersionMinimalV2 {
  /**
   * @type string, uuid
   */
  readonly id: GenericUuid;
}
export type ConfigurationResourceSoftwareItemMinimalV2 = ConfigurationResourceSoftwareVersionMinimalV2 & {
  /**
   * @type integer
   */
  readonly order: number;
  /**
   * @type string
   */
  readonly type: EnumConfigurationResourceSoftwareItem;
};
export type ConfigurationResourceRemoteKeyInjectionV2 = GenericUuid[];

export interface ConfigurationResourceConfigurationDefinitionMinimalV2 {
  /**
   * @type array
   */
  readonly applicationVersions: ConfigurationResourceSoftwareItemMinimalV2[];
  /**
   * @type object | undefined
   */
  readonly osVersion?: ConfigurationResourceSoftwareVersionMinimalV2;
  /**
   * @type array | undefined
   */
  readonly remoteKeyInjection?: ConfigurationResourceRemoteKeyInjectionV2;
  /**
   * @type array
   */
  readonly systemPackageVersions: ConfigurationResourceSoftwareItemMinimalV2[];
}
export interface ConfigurationResourceDefinitionMinimalV2 {
  /**
   * @type array
   */
  readonly compositions: ConfigurationResourceCompositionItemMinimalV2[];
  /**
   * @type object | undefined
   */
  readonly configuration?: ConfigurationResourceConfigurationDefinitionMinimalV2;
}
export interface ConfigurationResourcePatchV2 {
  /**
   * @type object | undefined
   */
  readonly definition?: ConfigurationResourceDefinitionMinimalV2;
  /**
   * @type string | undefined
   */
  readonly description?: string;
  /**
   * @type string | undefined
   */
  readonly name?: string;
}
export type ConfigurationResourceWriteV2 = ConfigurationResourcePatchV2 & object;
export interface DeploymentRequestResourceOptionConfigurationMinimalV2 {
  readonly custom?: ConfigurationResourceWriteV2;
  readonly reference?: DeploymentRequestResourceReferenceV2;
  /**
   * @type string, uuid
   */
  readonly snapshotId: GenericUuidReadOnly;
  /**
   * @type string
   */
  readonly type: EnumReferenceMode;
}
export interface DeploymentRequestResourceOptionDeleteV2 {
  /**
   * @type array
   */
  readonly applicationVersionsIds: GenericUuid[];
  /**
   * @type array
   */
  readonly filePaths: string[];
  /**
   * @type array
   */
  readonly packageIds: string[];
  /**
   * @type string, uuid
   */
  readonly snapshotId: GenericUuidReadOnly;
  /**
   * @type array
   */
  readonly systemPackageVersionsIds: GenericUuid[];
}
export interface DeploymentRequestResourceOptionMinimalV2 {
  /**
   * @type object | undefined
   */
  readonly configuration?: DeploymentRequestResourceOptionConfigurationMinimalV2;
  /**
   * @type object | undefined
   */
  readonly delete?: DeploymentRequestResourceOptionDeleteV2;
}

export interface DeploymentUnitBffResourceApi {
  readonly logicalDevice: DeviceChangeResourceLogicalDeviceApi;
  readonly merchantId: GenericIdReadOnlyApi;
  /**
   * @description Name of the merchant.
   * @type string
   */
  readonly merchantName: string;
  readonly physicalDevice: PhysicalDeviceResourceReadApi;
}

export type DeploymentUnitResourceItemV2 = DeploymentUnitBffResourceApi &
  DeploymentUnitResourceMinimalV2 & {
    /**
     * @type object
     */
    readonly options: DeploymentRequestResourceOptionMinimalV2;
  };
